import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import LoadingState from '../LoadingState.jsx';
import StatusBadge from '../StatusBadge.jsx';

/**
 * Can each enabled demand partner's declared delivery signal actually fire?
 *
 * WHY THIS PANEL EXISTS (2026-09-18)
 * ----------------------------------
 * GET /api/admin/stats/delivery-signal-health has been live since the overnight pass and had no UI
 * at all. A report nobody can see is the same as no report -- and this one answers a question that
 * decides whether BidDigi may describe its revenue as verified.
 *
 * The mechanic it surfaces: a partner declares a `delivery_signal` (burl / render_pixel / none) as
 * part of the readiness contract. `burl` and `render_pixel` are only ever fired by the pixel
 * injected into the winning creative, which is gated per partner by `render_confirmation_enabled`
 * (migration 030). If a partner declares one of those and that flag is off, the signal cannot fire
 * -- ever. Not "fires late": does not happen, and nothing reports that it did not. Revenue from
 * that partner is then partner-reported while the declaration implies it is independently
 * verified. That gap is what this panel makes impossible to miss.
 *
 * It sits at the top of the Demand Partners page rather than behind a tab or a nav item, because a
 * warning an operator has to go looking for is a warning that gets found after the invoice.
 */

/** Order the rows so the ones that need action are impossible to scroll past. */
function severityRank(p) {
  if (p.liveAndUnmeasurable) return 0;
  if (p.enabled && !p.declaredDeliverySignal) return 1;
  if (p.enabled) return 2;
  return 3;
}

function signalLabel(p) {
  if (!p.declaredDeliverySignal) return 'undeclared';
  return p.declaredDeliverySignal;
}

/** Deliberately not a raw count: "0 of 0" reads as fine and is not. */
function verdictTone(summary) {
  if (!summary) return 'neutral';
  if (summary.liveAndUnmeasurable > 0) return 'error';
  if (summary.liveDeclaringNone > 0) return 'paused';
  if (summary.enabled === 0) return 'neutral';
  return summary.anyConfirmationsInWindow ? 'active' : 'paused';
}

export default function DeliverySignalHealthPanel({ days = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.deliverySignalHealth(days)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : 'Could not load delivery signal health.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  if (loading) {
    return (
      <div className="card card--padded">
        <LoadingState label="Checking delivery signals" />
      </div>
    );
  }

  // A failed load is reported, never silently hidden. An empty panel where a warning belongs is
  // indistinguishable from "everything is fine", which is the one reading that must not happen.
  if (error) {
    return (
      <div className="card card--padded">
        <div className="card__title">Delivery signal health</div>
        <div className="card__hint card__hint--warning">
          {error} Until this loads, treat partner revenue as unverified rather than assuming it is fine.
        </div>
      </div>
    );
  }

  const { summary, verdict, partners = [] } = data || {};
  const tone = verdictTone(summary);
  const rows = [...partners].sort((a, b) => severityRank(a) - severityRank(b) || a.code.localeCompare(b.code));
  const enabledRows = rows.filter((p) => p.enabled);
  const shown = open ? rows : enabledRows;

  return (
    <div className="card">
      <div className="card__header card__header--padded">
        <div className="card__title">Delivery signal health</div>
        <StatusBadge status={tone === 'error' ? 'error' : tone === 'active' ? 'active' : tone === 'paused' ? 'paused' : 'unknown'} />
      </div>

      <div className="card__padded stack">
        {/* The backend's own sentence, rendered verbatim. It is written to be quotable in a
            partner or investor conversation, and paraphrasing it here would let the UI and the
            API disagree about what the platform is willing to claim. */}
        <div className={tone === 'error' ? 'card__hint card__hint--warning' : 'card__hint'}>
          {verdict || 'No verdict returned.'}
        </div>

        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-card__label">Enabled partners</div>
            <div className="kpi-card__value">{summary?.enabled ?? '—'}</div>
            <div className="kpi-card__sub">of {summary?.partners ?? '—'} in the catalog</div>
          </div>
          <div className={`kpi-card${summary?.liveAndUnmeasurable ? ' kpi-card--accent' : ''}`}>
            <div className="kpi-card__label">Live but unmeasurable</div>
            <div className="kpi-card__value">{summary?.liveAndUnmeasurable ?? '—'}</div>
            <div className="kpi-card__sub">declare a signal that cannot fire</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-card__label">Measurable</div>
            <div className="kpi-card__value">{summary?.liveAndMeasurable ?? '—'}</div>
            <div className="kpi-card__sub">signal can actually fire</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-card__label">Confirmations seen</div>
            <div className="kpi-card__value">{summary?.anyConfirmationsInWindow ? 'Yes' : 'None'}</div>
            <div className="kpi-card__sub">in the last {data?.windowDays ?? days} days</div>
          </div>
        </div>

        {/* "Can fire" and "has fired" are separate columns on purpose. A partner whose flag was
            switched on five minutes ago can fire and has not yet -- reporting that as healthy
            would be the same unearned confidence this whole report exists to remove. */}
        <table className="data-table">
          <thead>
            <tr>
              <th>Partner</th>
              <th>Declared signal</th>
              <th>Render pixel</th>
              <th>Can fire</th>
              <th>Tokens recorded</th>
              <th>Confirmed</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.code}>
                <td>
                  <div className="cell-title">{p.name || p.code}</div>
                  <div className="cell-subtitle">{p.code}</div>
                </td>
                <td><code className="code-chip">{signalLabel(p)}</code></td>
                <td>{p.renderConfirmationEnabled ? 'on' : <span className="cell-muted">off</span>}</td>
                <td>
                  {p.canFire === true && <StatusBadge status="active" />}
                  {p.canFire === false && <StatusBadge status="error" />}
                  {p.canFire == null && <span className="cell-muted">—</span>}
                </td>
                <td>{p.observed?.tokensRecorded ?? 0}</td>
                <td>
                  {p.observed?.tokensConfirmed ?? 0}
                  {p.observed?.lastConfirmedAt && (
                    <div className="cell-subtitle">
                      last {new Date(p.observed.lastConfirmedAt).toLocaleDateString()}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Keyed off `shown`, not `rows`. Off `rows` it explained partners the table was not
            displaying -- so with every enabled partner healthy it still rendered "Why these
            signals cannot fire", describing disabled rows the operator could not see. A warning
            that appears when nothing is wrong is how operators learn to ignore warnings.
            The reason strings are long and specific -- they name what to do, not just what is
            wrong -- so they live behind a disclosure rather than crowding the table. */}
        {shown.some((p) => p.canFire === false && p.reason) && (
          <details className="disclosure">
            <summary>Why these signals cannot fire</summary>
            <ul className="plain-list">
              {shown.filter((p) => p.canFire === false && p.reason).map((p) => (
                <li key={p.code}>
                  <strong>{p.name || p.code}</strong> — {p.reason}
                </li>
              ))}
            </ul>
          </details>
        )}

        {rows.length > enabledRows.length && (
          <button type="button" className="link-button" onClick={() => setOpen((v) => !v)}>
            {open
              ? `Show only enabled partners (${enabledRows.length})`
              : `Show all ${rows.length} partners, including disabled`}
          </button>
        )}
      </div>
    </div>
  );
}
